import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import { updateBondNumber } from "../../../../globals/utils/commons";
import { buildFullName } from "../../../../globals/utils/nameUtils";
import { increment } from "../../../../store/slices/countSlice";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import logger from "../../../../globals/utils/logger";
import { mapIndemnityReceivedToYesNo } from "../bondHelpers";
import {
  useBondRetrievalChat,
  useBondRetrievalChatUnread,
} from "./useBondRetrievalCore";
import { isoToDatetimeLocal, uploadToADLS } from "../bondRetrievalUtils";
import useBondRetrievalUnderwritingActions from "./useBondRetrievalUnderwritingActions";
import {
  calculateBlanketPremium,
  prefillBlanketBondForm,
  formatBondDate,
  computeBlanketExpirationDate,
  buildBlanketApprovalPayload,
  buildBlanketApprovalStatusUpdatePayload,
  buildBlanketUpdatePayload,
  resolveIssueDatePayload,
} from "../blanketBondRetrievalHelpers";

function useBlanketBondRetrieve() {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const [data, setData] = useState(null);
  const [editableCreatedAt, setEditableCreatedAt] = useState("");
  const navigate = useNavigate();
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const { openChat, setOpenChat, chatUnreadTotal, setChatUnreadTotal } = useBondRetrievalChat();
  const [billingMethod, setBillingMethod] = useState("");
  const [isIndemnityReceived, setIsIndemnityReceived] = useState("");
  const [state, setState] = useState(null);
  const [allCounties, setAllCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const { role } = useSelector((state) => state.auth);
  const disabled = role !== "super_user" && role !== "internal_user";
  const pii_access = userdetails?.is_pii_user;
  const [loaded, setLoaded] = useState(false);
  const { bond_id } = useParams();
  const [updateLoading, setUpdateLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [isUnderwritingLoading, setIsUnderwritingLoading] = useState(false);
  const [bondApproved, setBondApproved] = useState(false);
  const [pgId, setPgId] = useState(null);
  const [pgData, setPgData] = useState(null);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const [isEsignatureLoading, setIsEsignatureLoading] = useState(false);
  const username = useSelector((state) => state.auth?.user?.username) || "";
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [creditReportFile, setCreditReportFile] = useState(null);
  const [isCreditUploading, setIsCreditUploading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectFcraFile, setRejectFcraFile] = useState(null);
  const [rejectFcraUploading, setRejectFcraUploading] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const [newObligee, setNewObligee] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [showSSN, setShowSSN] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bondNum, setBondNum] = useState({ bond_number: null });
  const dispatch = useDispatch();

  useBondRetrievalChatUnread(bond_id, role, setChatUnreadTotal);

  const underwritingActions = useBondRetrievalUnderwritingActions({
    bond_id,
    userdetails,
    setIsUnderwritingLoading,
  });

  const updateBondNum = (updatedData) => {
    setBondNum(updatedData);
  };

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const cities = await apiClient("/fields/city/", "GET");
        const states = await apiClient("/fields/states/", "GET");
        setAllCities(cities);
        setAllStates(states);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      }
    };
    loadDropdownData();

    const savedState = localStorage.getItem("selectedState");
    if (savedState) {
      setState(JSON.parse(savedState));
    }
  }, []);

  useEffect(() => {
    const fetchCounties = async () => {
      if (state?.id) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(`/counties-by-state/${state.id}/`, "GET");
          const formattedCounties = countyResponse.map((county) => county.bond_county);
          setAllCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state]);

  const fetchBondData = async () => {
    try {
      const response = await apiClient(`/retrieve-blanket-bond/${bond_id}/`, "GET");
      if (!response) return;

      const { bond, blanketbond } = response;
      setData(response);
      const createdAt = response?.created_at ?? bond?.created_at;
      setEditableCreatedAt(isoToDatetimeLocal(createdAt));
      setBillingMethod(bond.billing_method);
      setIsIndemnityReceived(mapIndemnityReceivedToYesNo(blanketbond?.is_indemnity_received));

      const obligeesRes = await apiClient("/obligee-list-create/");
      setObligees(obligeesRes);
      prefillBlanketBondForm(setValue, bond, blanketbond, response);

      setPgId(blanketbond.pg_id);
      setLoaded(true);
      setBondNum({ bond_number: bond.bond_number });
    } catch (error) {
      console.error("Error fetching bond data:", error);
      toast.error("Failed to load bond data.");
      setLoaded(true);
    }
  };

  useEffect(() => {
    setLoaded(false);
    fetchBondData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bond_id, setValue]);

  useEffect(() => {
    async function fetchGuardianData() {
      if (pgId !== null) {
        const guardianData = await apiClient(`/professional-guardian-user/${pgId}/`);
        setPgData(guardianData);
      }
    }
    fetchGuardianData();
  }, [pgId]);

  const handleGoogleCallback = (address, city, stateValue, zipCode) => {
    setValue("address", address);
    setValue("city", city);
    setValue("state", stateValue);
    setValue("zipCode", zipCode);
  };

  const handleObligeeGoogleCallback = (address, city, stateValue, zipCode) => {
    setNewObligee((prev) => ({
      ...prev,
      street: address,
      city: allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
      state: allStates.find((s) => s.toLowerCase() === stateValue.toLowerCase()) || stateValue,
      zip: zipCode,
    }));
  };

  const handleAddObligee = async () => {
    try {
      setLoadingObligees(true);
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });

      setObligees((prev) => [...prev, response]);
      setValue("obligeeId", response.obligee_id);
      setOpenObligeeDialog(false);
      setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
      toast.success("Obligee added successfully!");
    } catch (error) {
      console.error("Error adding obligee:", error);
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  };

  const setApprovalLoadingState = (option) => {
    if (option.approval === "approval") {
      setApproveLoading(true);
    } else if (option.approval === "verification") {
      setVerificationLoading(true);
    }
  };

  const ensureAmsCustomerNumber = async (formData) => {
    if (!(pgId !== null && pgData?.ams_customer_number == null)) {
      return null;
    }

    const customerData = {
      firm_name: buildFullName(formData.firstName, formData.middleName, formData.lastName),
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip: formData.zipCode,
      email: formData.email,
      user_type: "pg",
      pg_id: pgId,
    };

    try {
      const response = await apiClient("/customer-creation-ams360", "POST", customerData);
      const customerNumber = response?.data?.customer_number ?? response?.customer_number;
      if (!customerNumber) return null;
      setPgData((prevData) => ({
        ...prevData,
        ams_customer_number: customerNumber,
      }));
      return customerNumber;
    } catch (error) {
      console.error("Error creating customer in AMS 360:", error);
      return null;
    }
  };

  const resolveBondNumber = async (formData) => {
    let bondNumber = formData.number?.bond_number;

    const oldBondNumber = formData.number?.bondno_id || null;
    const newBondNumber = bondNum.bond_number?.bondno_id || null;

    if (!((formData.number === null && bondApproved === false) || (oldBondNumber !== newBondNumber))) {
      return bondNumber;
    }

    bondNumber = bondNum.bond_number.bond_number;
    await updateBondNumber(
      bondNum.bond_number.bondno_id,
      bond_id,
      `${buildFullName(formData.firstName, formData.middleName, formData.lastName)} (Blanket Bond)`
    );
    setBondApproved(true);
    return bondNumber;
  };

  const resolveCustomerNo = (pgDataValue, customerNumberAms) => {
    const rawCustomerNo = pgDataValue?.ams_customer_number ?? customerNumberAms;
    return rawCustomerNo != null && rawCustomerNo !== ""
      ? Number.parseInt(String(rawCustomerNo), 10)
      : null;
  };

  const handleApprove = async (formData, option) => {
    setApprovalLoadingState(option);

    const customerNumberAms = await ensureAmsCustomerNumber(formData);
    const currentDate = formatBondDate(new Date());
    const bondNumber = await resolveBondNumber(formData);
    const expirationDate = computeBlanketExpirationDate(formData);

    const issueDatePayload = resolveIssueDatePayload(option, formData, currentDate);
    const expirationDatePayload =
      option.expiration_date != null && option.expiration_date !== ""
        ? option.expiration_date
        : expirationDate;

    const customerNo = resolveCustomerNo(pgData, customerNumberAms);

    if (option.ams_flow && (!customerNo || Number.isNaN(customerNo))) {
      setApproveLoading(false);
      setVerificationLoading(false);
      toast.error(
        "AMS customer number is missing or invalid. Cannot create policy in AMS360."
      );
      return;
    }

    try {
      const payload = buildBlanketApprovalPayload(formData, option, {
        bondId: Number.parseInt(bond_id),
        bondNumber,
        customerNo,
        currentDate,
        issueDatePayload,
        expirationDatePayload,
        pgData,
        userFullName: `${userdetails.first_name} ${userdetails.last_name}`,
      });

      const docModule = option.approval === "verification" ? "vermost_rider" : "bond_blanket_word";
      const dataForPost = {
        module: docModule,
        process: "main",
        email_list: [
          "FazlanF@blueoceansp.ai",
          "FouzaanMH@blueoceansp.ai",
          "shaznym@blueoceansp.ai",
          "PrabudevaU@blueoceansp.ai",
        ],
        payload,
      };
      await apiClient("/approve-blanket-bond/", "POST", dataForPost);
      await apiClient(
        `/update-blanket-bond/${bond_id}/`,
        "PATCH",
        buildBlanketApprovalStatusUpdatePayload(option)
      );
      setApproveLoading(false);
      setVerificationLoading(false);
      if (option.approval === "approval") {
        toast.success("Bond Sent for Approval Successfully");
      } else if (option.approval === "verification") {
        toast.success("Bond Sent for Renewal Successfully");
      }
      dispatch(increment());
    } catch (error) {
      setApproveLoading(false);
      const message = extractErrorMessage(error);
      toast.error(`Bond approval failed. ${message}`);
      console.error("Error approving blanket bond:", error);
    }
  };

  const handleReject = async (dataOrFcraOverride) => {
    try {
      setRejectLoading(true);
      const blanketbondPayload =
        dataOrFcraOverride &&
        typeof dataOrFcraOverride === "object" &&
        (dataOrFcraOverride.fcra_document || dataOrFcraOverride.fcra_document_link)
          ? {
              fcra_document: dataOrFcraOverride.fcra_document || "",
              fcra_document_link: dataOrFcraOverride.fcra_document_link || "",
            }
          : {};
      await apiClient(`/update-blanket-bond/${bond_id}/`, "PATCH", {
        bond: {
          status: "Rejected",
          updated_at: new Date().toISOString(),
        },
        blanketbond: blanketbondPayload,
      });
      setRejectLoading(false);
      setRejectDialogOpen(false);
      setRejectFcraFile(null);
      toast.success("Bond Rejected Successfully");
      dispatch(increment());
      setTimeout(function () {
        window.close();
      }, 2000);
    } catch (error) {
      logger.warn("useBlanketBondRetrieve", "reject bond failed", { err: error });
      setRejectLoading(false);
      toast.error("Failed to reject bond.");
    }
  };

  const onSubmit = async (formData) => {
    setUpdateLoading(true);
    try {
      const payload = buildBlanketUpdatePayload(formData, {
        billingMethod,
        isIndemnityReceived,
        editableCreatedAt,
      });

      const oldBondNumber = formData.number?.bondno_id || null;
      const newBondNumber = bondNum.bond_number?.bondno_id || null;

      if (oldBondNumber != null && newBondNumber != null && oldBondNumber !== newBondNumber) {
        await updateBondNumber(
          newBondNumber,
          bond_id,
          `${buildFullName(formData.firstName, formData.middleName, formData.lastName)} (Blanket Bond)`
        );
        setBondApproved(true);
      }

      await apiClient(`/update-blanket-bond/${bond_id}/`, "PATCH", payload);
      toast.success("Bond updated successfully!");
      setUpdateLoading(false);
      dispatch(increment());
    } catch (error) {
      setUpdateLoading(false);
      console.error("Error submitting form:", error);
      const message = extractErrorMessage(error);
      toast.error(`Failed to update bond. ${message}`);
    }
  };

  const handleUpdateUnderwriting = async (status) => {
    try {
      if (status === "approved") {
        setIsApproveLoading(true);
      } else if (status === "rejected") {
        setIsRejectLoading(true);
      }
      setIsUnderwritingLoading(true);

      await apiClient(`/update_underwriting_status/${bond_id}/`, "PATCH", { status });
      toast.success(`Underwriting ${status} successfully!`);
    } catch (error) {
      logger.warn("useBlanketBondRetrieve", "underwriting status update failed", { err: error });
      toast.error(`Failed to ${status} underwriting. Please try again.`);
    } finally {
      setIsApproveLoading(false);
      setIsRejectLoading(false);
      setIsUnderwritingLoading(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleApproveOption = (option) => {
    handleSubmit((formData) => handleApprove(formData, option))();
    handleMenuClose();
  };

  const [renewalsAnchorEl, setRenewalsAnchorEl] = useState(null);
  const [verificationCertDialogOpen, setVerificationCertDialogOpen] = useState(false);
  const [verificationCertFormData, setVerificationCertFormData] = useState(null);
  const [verificationCertEffectiveDate, setVerificationCertEffectiveDate] = useState("");
  const [verificationCertExpirationDate, setVerificationCertExpirationDate] = useState("");
  const [verificationCertBondTerm, setVerificationCertBondTerm] = useState("1 Year Bond");

  const BOND_TERM_OPTIONS = ["1 Year Bond", "2 Year Bond", "3 Year Bond"];

  const handleVerificationCertificate = () => {
    handleSubmit((formData) => {
      const baseDate = formData.doe ? new Date(formData.doe) : new Date();
      const safeBase = Number.isNaN(baseDate.getTime()) ? new Date() : baseDate;
      const bondTermMatch = (formData.bondTerm || "").match(/^(\d+)\s*[Yy]ear\s*[Bb]ond$/);
      const bondYears = bondTermMatch ? Number.parseInt(bondTermMatch[1], 10) : 0;
      const expDate = new Date(safeBase);
      expDate.setFullYear(expDate.getFullYear() + bondYears);
      const toYMD = (d) => d.toISOString().slice(0, 10);
      setVerificationCertFormData(formData);
      setVerificationCertBondTerm(formData.bondTerm || "1 Year Bond");
      setVerificationCertEffectiveDate(formData.doe ? toYMD(new Date(formData.doe)) : toYMD(new Date()));
      setVerificationCertExpirationDate(toYMD(expDate));
      setVerificationCertDialogOpen(true);
    })();
    setRenewalsAnchorEl(null);
  };

  const ymdToMdy = (ymd) => {
    if (!ymd) return "";
    const parts = ymd.split("-");
    if (parts.length !== 3) return ymd;
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  };

  const handleVerificationCertConfirm = async () => {
    if (!verificationCertFormData) return;
    const effectiveMdy = ymdToMdy(verificationCertEffectiveDate);
    const expirationMdy = ymdToMdy(verificationCertExpirationDate);
    setVerificationCertDialogOpen(false);

    const bondTermChanged = verificationCertBondTerm !== (verificationCertFormData.bondTerm || "");
    if (bondTermChanged) {
      try {
        setVerificationLoading(true);
        await apiClient(`/update-blanket-bond/${bond_id}/`, "PATCH", {
          bond: {
            bond_sub_category: verificationCertBondTerm,
            premium: calculateBlanketPremium(verificationCertBondTerm),
            updated_at: new Date().toISOString(),
          },
          blanketbond: {
            bond_term: verificationCertBondTerm,
            updated_at: new Date().toISOString(),
          },
        });
        toast.success("Bond term updated.");
      } catch (error) {
        setVerificationLoading(false);
        const message = extractErrorMessage(error);
        toast.error(`Failed to update bond term. ${message}`);
        return;
      }
    }

    const formDataWithTerm = { ...verificationCertFormData, bondTerm: verificationCertBondTerm };
    handleApprove(formDataWithTerm, {
      approval: "verification",
      billing_method: billingMethod || "Agency Bill",
      ams_flow: false,
      effective_date: effectiveMdy,
      expiration_date: expirationMdy,
    });
    setVerificationCertFormData(null);
  };

  const handleSignIndemnity = async () => {
    setIsEsignatureLoading(true);
    try {
      await apiClient(`/bond-signature/${bond_id}/`, "POST");
      toast.success("E-signature request sent successfully");
    } catch (error) {
      toast.error("Failed to send e-signature request.");
      console.error(error);
    } finally {
      setIsEsignatureLoading(false);
    }
  };

  const runCreditReport = async (bondId, file) => {
    const formData = new FormData();
    formData.append("credit_report_document", file);

    return await apiClient(
      `/run-credit-report/?bond_id=${bondId}`,
      "POST",
      formData,
      {},
      { "Content-Type": "multipart/form-data" }
    );
  };

  return {
    handleSubmit,
    control,
    watch,
    setValue,
    errors,
    navigate,
    bond_id,
    role,
    disabled,
    pii_access,
    userdetails,
    username,
    data,
    loaded,
    bondNum,
    pgId,
    pgData,
    allCities,
    allStates,
    allCounties,
    loadingCounties,
    obligees,
    loadingObligees,
    state,
    setState,
    billingMethod,
    setBillingMethod,
    isIndemnityReceived,
    setIsIndemnityReceived,
    editableCreatedAt,
    setEditableCreatedAt,
    openChat,
    setOpenChat,
    chatUnreadTotal,
    showSSN,
    setShowSSN,
    updateLoading,
    approveLoading,
    verificationLoading,
    rejectLoading,
    isUnderwritingLoading,
    isApproveLoading,
    isRejectLoading,
    isEsignatureLoading,
    isCreditUploading,
    setIsCreditUploading,
    rejectFcraUploading,
    setRejectFcraUploading,
    anchorEl,
    underwritingAnchorEl: underwritingActions.underwritingAnchorEl,
    renewalsAnchorEl,
    setRenewalsAnchorEl,
    rejectDialogOpen,
    setRejectDialogOpen,
    rejectFcraFile,
    setRejectFcraFile,
    isCreditDialogOpen,
    setIsCreditDialogOpen,
    creditReportFile,
    setCreditReportFile,
    openObligeeDialog,
    setOpenObligeeDialog,
    newObligee,
    setNewObligee,
    overrideReasonDialogOpen: underwritingActions.overrideReasonDialogOpen,
    setOverrideReasonDialogOpen: underwritingActions.setOverrideReasonDialogOpen,
    overrideReason: underwritingActions.overrideReason,
    setOverrideReason: underwritingActions.setOverrideReason,
    manualUnderwritingDialogOpen: underwritingActions.manualUnderwritingDialogOpen,
    setManualUnderwritingDialogOpen: underwritingActions.setManualUnderwritingDialogOpen,
    manualUnderwritingReason: underwritingActions.manualUnderwritingReason,
    setManualUnderwritingReason: underwritingActions.setManualUnderwritingReason,
    verificationCertDialogOpen,
    setVerificationCertDialogOpen,
    verificationCertFormData,
    setVerificationCertFormData,
    verificationCertEffectiveDate,
    setVerificationCertEffectiveDate,
    verificationCertExpirationDate,
    setVerificationCertExpirationDate,
    verificationCertBondTerm,
    setVerificationCertBondTerm,
    BOND_TERM_OPTIONS,
    handleGoogleCallback,
    handleObligeeGoogleCallback,
    handleAddObligee,
    calculatePremium: calculateBlanketPremium,
    handleApprove,
    handleReject,
    onSubmit,
    handleUpdateUnderwriting,
    handleMenuClick,
    handleMenuClose,
    handleApproveOption,
    handleUnderwritingMenuClick: underwritingActions.handleUnderwritingMenuClick,
    handleUnderwritingMenuClose: underwritingActions.handleUnderwritingMenuClose,
    handleOverrideUnderwritingClick: underwritingActions.handleOverrideUnderwritingClick,
    handleManualUnderwritingClick: underwritingActions.handleManualUnderwritingClick,
    handleSubmitManualUnderwriting: underwritingActions.handleSubmitManualUnderwriting,
    handleSubmitOverrideReason: underwritingActions.handleSubmitOverrideReason,
    handleVerificationCertificate,
    handleVerificationCertConfirm,
    handleSignIndemnity,
    runCreditReport,
    uploadToADLS,
    updateBondNum,
  };
}

export default useBlanketBondRetrieve;
